import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { BRANDS } from "@/lib/constants";

const NAV = [
  { label: "Beranda", to: "/" },
  { label: "Katalog", to: "/katalog" },
  { label: "Cara Beli & Cicilan", to: "/cara-pembelian" },
  { label: "Tentang Kami", to: "/tentang-kami" },
  { label: "Kontak", to: "/kontak" },
];

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
      <img src="/assets/logo.png" alt="BigBerry Store" className="h-10 w-10 rounded-lg" />
      <div className="leading-none">
        <span className="block font-heading text-lg font-700 tracking-tight text-white">
          BigBerry
        </span>
        <span className="block text-[10px] uppercase tracking-[0.2em] text-berry">Store</span>
      </div>
    </Link>
  );
}

export default function Navbar() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/katalog?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0B0C10]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3.5 sm:px-6">
        <Logo />

        <nav className="ml-6 hidden items-center gap-6 lg:flex">
          {NAV.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
                className={`text-sm font-medium transition-colors hover:text-white ${
                  active ? "text-white" : "text-gray-400"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden max-w-xs flex-1 md:block">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              data-testid="nav-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari produk, brand..."
              className="border-white/10 bg-white/5 pl-9 text-sm text-white placeholder:text-gray-500 focus-visible:ring-berry"
            />
          </div>
        </form>

        <div className="ml-auto md:ml-0 lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-mobile-toggle" aria-label="Menu" className="rounded-lg p-2 text-white hover:bg-white/10">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] border-white/10 bg-[#0B0C10] text-white">
              <div className="mb-6 flex items-center justify-between">
                <Logo />
              </div>
              <form onSubmit={submitSearch} className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <Input
                    data-testid="mobile-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Cari produk..."
                    className="border-white/10 bg-white/5 pl-9 text-white placeholder:text-gray-500"
                  />
                </div>
              </form>
              <nav className="flex flex-col gap-1">
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white"
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="mt-6 border-t border-white/10 pt-4">
                <p className="mb-2 px-3 text-xs uppercase tracking-widest text-gray-500">Brand</p>
                <div className="flex flex-wrap gap-2 px-1">
                  {BRANDS.map((b) => (
                    <Link
                      key={b}
                      to={`/brand/${encodeURIComponent(b)}`}
                      onClick={() => setOpen(false)}
                      className="rounded-full border border-white/10 px-3 py-1 text-xs text-gray-300 hover:border-berry hover:text-white"
                    >
                      {b}
                    </Link>
                  ))}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="hidden border-t border-white/5 lg:block">
        <div className="mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto px-4 py-2 sm:px-6">
          {BRANDS.map((b) => (
            <Link
              key={b}
              to={`/brand/${encodeURIComponent(b)}`}
              data-testid={`nav-brand-${b}`}
              className="whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium text-gray-400 transition-colors hover:bg-white/5 hover:text-white"
            >
              {b}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}
